import { useMemo } from 'react';
import { bindActionCreators, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { gameApi } from '@root/store/api/game';
import appReducer, {
  APP_SLICE_KEY,
  appActions,
} from '@root/store/slices/appSlice';
import balanceReducer, {
  BALANCE_SLICE_KEY,
  balanceActions,
} from '@root/store/slices/balanceSlice';
import betReducer, {
  BET_SLICE_KEY,
  betActions,
} from '@root/store/slices/betSlice';
import betValuesReducer, {
  BET_VALUES_SLICE_KEY,
  betValuesActions,
} from '@root/store/slices/betValuesSlice';
import fundingReducer, {
  FUNDING_SLICE_KEY,
  fundingActions,
} from '@root/store/slices/fundingSlice';
import gameReducer, {
  GAME_SLICE_KEY,
  gameActions,
} from '@root/store/slices/gameSlice';
import journeyReducer, {
  JOURNEY_SLICE_KEY,
  journeyActions,
} from '@root/store/slices/journeySlice';
import marketReducer, {
  MARKET_SLICE_KEY,
  marketActions,
} from '@root/store/slices/marketSlice';
import overlayReducer, {
  OVERLAY_SLICE_KEY,
  overlayActions,
} from '@root/store/slices/overlaySlice';
import phaserReducer, {
  PHASER_SLICE_KEY,
  phaserActions,
} from '@root/store/slices/phaserSlice';
import roundReducer, {
  ROUND_SLICE_KEY,
  roundActions,
} from '@root/store/slices/roundSlice';
import { authApi } from '@shared/store/api/auth';
import { styxApi } from '@shared/store/api/styx';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';

export const store = configureStore({
  reducer: {
    [APP_SLICE_KEY]: appReducer,
    [BALANCE_SLICE_KEY]: balanceReducer,
    [BET_SLICE_KEY]: betReducer,
    [BET_VALUES_SLICE_KEY]: betValuesReducer,
    [FUNDING_SLICE_KEY]: fundingReducer,
    [GAME_SLICE_KEY]: gameReducer,
    [JOURNEY_SLICE_KEY]: journeyReducer,
    [MARKET_SLICE_KEY]: marketReducer,
    [OVERLAY_SLICE_KEY]: overlayReducer,
    [PHASER_SLICE_KEY]: phaserReducer,
    [ROUND_SLICE_KEY]: roundReducer,
    [authApi.reducerPath]: authApi.reducer,
    [gameApi.reducerPath]: gameApi.reducer,
    [styxApi.reducerPath]: styxApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      gameApi.middleware,
      styxApi.middleware
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type RootStore = typeof store;
export type AppDispatch = typeof store.dispatch;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

setupListeners(store.dispatch);

export function useActions() {
  const dispatch = useDispatch();
  return useMemo(
    () =>
      bindActionCreators(
        {
          ...appActions,
          ...balanceActions,
          ...betActions,
          ...betValuesActions,
          ...fundingActions,
          ...gameActions,
          ...journeyActions,
          ...marketActions,
          ...overlayActions,
          ...phaserActions,
          ...roundActions,
        },
        dispatch
      ),
    [dispatch]
  );
}

export {
  useOneTimeTokenLoginMutation,
  useRefreshTokenMutation,
} from '@shared/store/api/auth';

export {
  useGetGamePreferencesQuery,
  useLazyGetDefaultGamePreferencesQuery,
  useSaveGamePreferencesMutation,
  useGetGameHistoryQuery,
  useGetGameRulesV2Query,
  useGetUserRoundHistoryQuery,
  useGetRunHistoryQuery,
  useGetGameInactivityConfigQuery,
} from './api/game';
